import axios from 'axios';

function showRecovery(window, status) {
  const { document } = window;
  const dialog = document.createElement('dialog');
  dialog.id = 'zotel-session-recovery';
  dialog.setAttribute('aria-labelledby', 'zotel-session-title');
  dialog.setAttribute('aria-describedby', 'zotel-session-description');
  const style = document.createElement('style');
  style.textContent = `
    #zotel-session-recovery { box-sizing:border-box; width:min(460px,calc(100% - 32px)); margin:auto; padding:28px; border:1px solid #e2e8f0; border-radius:16px; background:#fff; color:#172033; box-shadow:0 24px 80px #0003; font:15px/1.6 system-ui,sans-serif; }
    #zotel-session-recovery::backdrop { background:rgba(15,23,42,.45); }
    #zotel-session-recovery h2 { margin:0 0 12px; font-size:22px; font-weight:650; line-height:1.3; }
    #zotel-session-recovery p { margin:0 0 16px; color:#475569; }
    #zotel-session-recovery button, #zotel-session-reminder { padding:10px 16px; border:1px solid #cbd5e1; border-radius:8px; cursor:pointer; font:600 14px/1.5 system-ui,sans-serif; }
    #zotel-session-recovery button { width:100%; margin-top:8px; background:#fff; color:#334155; }
    #zotel-session-recovery button[data-refresh], #zotel-session-reminder { background:#172033; color:#fff; }
    #zotel-session-reminder { position:fixed; bottom:20px; right:20px; max-width:calc(100% - 40px); z-index:2147483646; box-shadow:0 4px 16px #0003; }
    #zotel-session-reminder[hidden] { display:none; }
    #zotel-session-recovery button:focus-visible, #zotel-session-reminder:focus-visible { outline:3px solid #2563eb; outline-offset:3px; }
  `;
  const heading = document.createElement('h2');
  heading.id = 'zotel-session-title';
  const description = document.createElement('p');
  description.id = 'zotel-session-description';
  const warning = document.createElement('p');
  warning.textContent = 'Unsaved changes may be lost when you continue. Review or copy your work first.';
  const refresh = document.createElement('button');
  refresh.type = 'button';
  refresh.setAttribute('data-refresh', '');
  // A GET of the current page lets the normal auth middleware save the intended
  // destination and check property access. Never replay the failed mutation.
  refresh.addEventListener('click', () => window.location.reload());
  const review = document.createElement('button');
  review.type = 'button';
  review.textContent = 'Review unsaved changes';
  const reminder = document.createElement('button');
  reminder.id = 'zotel-session-reminder';
  reminder.type = 'button';
  reminder.hidden = true;
  const dismiss = () => {
    dialog.close();
    reminder.hidden = false;
  };
  review.addEventListener('click', dismiss);
  dialog.addEventListener('cancel', (event) => {
    event.preventDefault();
    dismiss();
  });
  reminder.addEventListener('click', () => {
    reminder.hidden = true;
    dialog.showModal();
  });
  dialog.append(style, heading, description, warning, refresh, review);
  document.body.append(dialog, reminder);
  const update = (nextStatus) => {
    heading.textContent = nextStatus === 401 ? 'Please sign in again' : 'Your session needs refreshing';
    description.textContent = nextStatus === 401
      ? 'Your sign-in is no longer valid. Sign in again to continue on this page.'
      : 'Refresh to continue. You may need to sign in again.';
    refresh.textContent = nextStatus === 401 ? 'Sign in again' : 'Refresh and continue';
    reminder.textContent = nextStatus === 401 ? 'Sign in to continue' : 'Session needs refreshing';
  };
  update(status);
  dialog.showModal();
  review.focus();
  return { update, remove: () => { dialog.remove(); reminder.remove(); } };
}

export function installSessionRecovery(window, render = showRecovery) {
  if (window.zotelSessionRecovery) return window.zotelSessionRecovery;

  let recovery;
  let authenticationLost = false;
  let livewireInstalled = false;
  const axiosClients = new WeakSet();
  const handle = (status) => {
    if (status !== 419 && status !== 401) return false;
    authenticationLost ||= status === 401;
    const effectiveStatus = authenticationLost ? 401 : 419;
    if (!recovery) recovery = render(window, effectiveStatus);
    else recovery.update(effectiveStatus);
    return true;
  };
  const isSameOrigin = (url) => {
    try {
      return new URL(url, window.location.href).origin === window.location.origin;
    } catch {
      return false;
    }
  };
  const installAxios = (axios) => {
    if (axiosClients.has(axios)) return;
    axiosClients.add(axios);
    axios.interceptors.response.use((response) => response, (error) => {
      if (isSameOrigin(axios.getUri(error.config || {}))) handle(error.response?.status);
      return Promise.reject(error);
    });
  };
  const installLivewire = () => {
    if (livewireInstalled || !window.Livewire) return;
    livewireInstalled = true;
    window.Livewire.hook('request', ({ fail }) => {
      fail(({ status, preventDefault }) => {
        if (handle(status)) preventDefault();
      });
    });
  };
  const nativeFetch = window.fetch.bind(window);
  window.fetch = async (input, init) => {
    const response = await nativeFetch(input, init);
    if (isSameOrigin(typeof input === 'object' && 'url' in input ? input.url : String(input))) {
      handle(response.status);
    }
    return response;
  };
  window.zotelSessionRecovery = { handle, installAxios };
  window.document.addEventListener('livewire:init', installLivewire, { once: true });
  window.document.addEventListener('livewire:navigated', () => {
    recovery?.remove();
    recovery = undefined;
    authenticationLost = false;
  });
  installLivewire();
  return window.zotelSessionRecovery;
}

if (typeof window !== 'undefined') installSessionRecovery(window).installAxios(axios);
